import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import remove from 'lodash/remove';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import { Color3 } from '@babylonjs/core/Maths/math';

import { projectInterface } from './interface';
import { Observable } from '../../Utils/observable';
import { getRandomString } from '../../Utils/math';
import { colorBase255toBase1 } from '../../Utils/color';
import { getAllTextureUrlLow, IDamTextures } from '../../Utils/dam';

export interface IDamMaterial {
    damName?: string;
    damMaterial?: string;
}

export interface IMaterialOptions extends IDamTextures, IDamMaterial {
    key?: string;
    colorName?: string;
    name?: string;
    icone?: string;
    textureScaling?: number;
    textureRotation?: number;
    alpha?: number;
    metallic?: number;
    roughness?: number;
    emissive?: number;
    tintColor?: number[];
    bumpHeight?: number;
}

projectInterface.material = {
    key: 'key',
    name: 'mn',
    icone: 'mic',
    textureScaling: 'mt',
    textureRotation: 'mtr',
    alpha: 'mal',
    metallic: 'mme',
    roughness: 'mro',
    emissive: 'mem',
    tintColor: 'mtc',
    bumpHeight: 'mwbh',
    albedoTexture: 'mate',
    metallicTexture: 'mmte',
    bumpTexture: 'mbte',
    ambientTexture: 'mabte',
    damName: 'madn',
    damMaterial: 'madm',
};

const EMPTY_MATERIAL = {
    tintColor: null,
    roughness: null,
    metallic: null,
    bumpHeight: null,
    albedoTexture: null
};

export const DEFAULT_MATERIAL_COLOR = [255, 85, 0];

export const getColorFromMaterial = (
    material: IMaterialOptions,
    rgbonly?: boolean,
    useIcone?: boolean
): string => {
    if (material.icone && !rgbonly && useIcone !== false) {
        return `url('${material.icone}')`;
    }
    let color = material.tintColor;
    // Formerly used albedo and emissive
    if (!color) color = material.emissiveColor;
    if (!color) color = material.albedoColor;

    if (color) {
        // Apply Emisive Ratio
        const { emissive } = material;
        if (emissive !== undefined) {
            // This ratio works pretty well to match emissive 3D change
            const ratio = 1 + (Math.sqrt(emissive));
            color = [color[0] * ratio, color[1] * ratio, color[2] * ratio];
        }
        // Manage conditions
        if (rgbonly) {
            return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
        }
        let gradient = '';
        if (material.metallic && material.metallic > 0.5) {
            const color1 = `rgb(${color[0] - 90}, ${color[1] - 90}, ${color[2] - 90})`;
            const color2 = `rgb(${color[0] + 35}, ${color[1] + 35}, ${color[2] + 35})`;
            gradient = `linear-gradient(45deg, ${color1} 0%, ${color1} 20%, ${color2} 33%, ${color2} 36%, ${color1} 46%, ${color1} 50%, ${color2} 59%, ${color2} 65%,  ${color1} 85%,  ${color1} 100%)`;
        } else {
            const color1 = `rgb(${color[0] + 60}, ${color[1] + 60}, ${color[2] + 60})`;
            const color2 = `rgb(${color[0] - 60}, ${color[1] - 60}, ${color[2] - 60})`;
            gradient = `linear-gradient(45deg, ${color1}, ${color2})`;
        }
        if (material.albedoTexture) gradient += `, url(${material.albedoTexture})`;
        return gradient;
    }
    return 'white';
};

export const isMaterialUsingDam = (material: IMaterialOptions): boolean => {
    const { damName, damMaterial } = material;
    const hasDamName = damName !== undefined && damName !== null && damName !== '';
    const hasDamMaterial = damMaterial !== undefined && damMaterial !== null && damMaterial !== '';
    return hasDamName && hasDamMaterial;
};

const updateDamTexture = (newMat: IMaterialOptions): IMaterialOptions => {
    if (isMaterialUsingDam(newMat)) {
        const textures = getAllTextureUrlLow(newMat.damMaterial, newMat.damName);
        newMat = { ...newMat, ...textures };
    }
    return newMat;
};

// MaterialEvent and not TouchEvent otherwise conflict with real window TouchEvent
export enum MaterialEvent {
    Add,
    RemoveFromVariant,
    RemoveFromAll,
    Update,
}

export class ToolMaterial extends Observable<MaterialEvent, IMaterialOptions> {
    constructor() {
        super('MATERIAL');
    }

    private _materialList: IMaterialOptions[] = [];

    public get materialList(): IMaterialOptions[] {
        return this._materialList;
    }

    public set materialList(value: IMaterialOptions[]) {
        this._materialList = value;
    }

    public getMaterialListToSave(): IMaterialOptions[] {
        const newMaterials = cloneDeep(this._materialList);
        const keys = Object.keys(newMaterials);
        keys.forEach((k) => {
            const material = newMaterials[k];
            // We don't save textures link if from dam
            if (isMaterialUsingDam(material)) {
                delete material.albedoTexture;
                delete material.metallicTexture;
                delete material.bumpTexture;
            }
        });
        return newMaterials;
    }

    public getMaterialKeys(): string[] {
        return map(this._materialList, 'key');
    }

    public addMaterial(): IMaterialOptions {
        const newMaterial = {
            key: getRandomString(),
            tintColor: DEFAULT_MATERIAL_COLOR
        };
        this.materialList.push({ ...newMaterial });
        this.notify(MaterialEvent.Add, { ...newMaterial });
        return { ...newMaterial };
    }

    public updateColorName(material: IMaterialOptions, name: string): IMaterialOptions {
        const idx = findIndex(this.materialList, (m) => m.key === material.key);
        material.colorName = name;
        this.materialList[idx] = material;
        return material;
    }

    public setMaterialProperties(
        materialKey: string,
        newProps: IMaterialOptions
    ): IMaterialOptions {
        const material = this.getMaterialByKey(materialKey);
        let updatedMaterial = { ...material, ...newProps };
        updatedMaterial = updateDamTexture(updatedMaterial);
        const idx = findIndex(this.materialList, (m) => m.key === material.key);
        this.materialList[idx] = updatedMaterial;
        this.notify(MaterialEvent.Update, updatedMaterial);
        return updatedMaterial;
    }

    public removeMaterial(materialKey: string): void {
        const material = remove(this.materialList, (m) => m.key === materialKey);
        this.notify(MaterialEvent.RemoveFromAll, material);
        // So that variant icon also update color
        this.notify(MaterialEvent.Update, material);
    }

    // toLinearSpace() is very important to match hexadecimal color in 3D
    // See https://forum.babylonjs.com/t/pbrmaterial-albedo-color-rgb-in-playground-code-different-than-rgb-shown-in-inspector-and-on-object/9675/6
    public getMaterialWithLinearSpaceByKey(materialKey: string): IMaterialOptions {
        const material = this.getMaterialByKey(materialKey);
        if (material.tintColor) {
            const tint = material.tintColor;
            const fixedTint = colorBase255toBase1(tint);
            const tintColor3 = new Color3(fixedTint[0], fixedTint[1], fixedTint[2]);
            const linearTint = tintColor3.toLinearSpace();
            const linearTintColor = [linearTint.r * 255, linearTint.g * 255, linearTint.b * 255];
            material.tintColor = linearTintColor;
        }

        return material;
    }

    public getMaterialByKey(key: string): IMaterialOptions {
        const mat = find(this.materialList, (m) => m.key === key) || EMPTY_MATERIAL;
        let newMat = { ...mat };
        newMat = updateDamTexture(newMat);
        return newMat;
    }

    public removeDamTextures(key: string): void {
        const mat = this.getMaterialByKey(key);
        if (isMaterialUsingDam(mat)) {
            // Remove texture link from DAM
            const newMat = {
                damMaterial: null,
                albedoTexture: null,
                metallicTexture: null,
                bumpTexture: null,
            };
            this.setMaterialProperties(key, newMat);
        }
    }

    public getMaterialByName(name: string): IMaterialOptions {
        const mat = find(this.materialList, (m) => m.name === name) || EMPTY_MATERIAL;
        return { ...mat };
    }

    public getColorFromMaterialKey(
        materialKey: string,
        rgbonly?: boolean,
        useIcone?: boolean
    ): string {
        const material = this.getMaterialByKey(materialKey);
        if (!material) return '';
        return getColorFromMaterial(material, rgbonly, useIcone);
    }
}
